import React, { useState } from "react";
import { toast } from "sonner";
import { Briefcase, HardHat, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { authApi } from "../services/authApi";
import { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { useLocalization } from "../hooks/useLocalization";
import { useNavigate } from "react-router-dom";

/**
 * Dual-purpose MANAGEMENT accounts with a linked employee record can switch their
 * active portal context here. Never rendered for STAFF/CUSTOMER-domain accounts or
 * for MANAGEMENT accounts with no linked employee profile.
 */
export const PortalSwitcher = () => {
  const { user, applyAuthenticatedUser } = useAuth();
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  if (!user || user.account_domain !== "MANAGEMENT" || !user.employee_id) return null;

  const handleSwitch = async (portal) => {
    if (portal === user.active_portal || switching) return;
    setSwitching(true);
    try {
      const { data } = await authApi.switchPortal(portal);
      applyAuthenticatedUser(data);
      toast.success(portal === "STAFF" ? t("portal.switchedToStaff") : t("portal.switchedToManagement"));
      navigate(portal === "STAFF" ? "/employee" : "/management", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSwitching(false);
    }
  };

  const isStaff = user.active_portal === "STAFF";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="portal-switcher-trigger"
          disabled={switching}
          aria-label={isStaff ? t("portal.staffPortal") : t("portal.managementPortal")}
          className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors active:scale-95 disabled:opacity-50"
        >
          {isStaff ? <HardHat size={14} className="text-amber-600" /> : <Briefcase size={14} className="text-blue-600" />}
          <span className="hidden sm:inline">{isStaff ? t("portal.staffPortal") : t("portal.managementPortal")}</span>
          <ChevronDown size={14} className="text-gray-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="portal-switcher-menu">
        <DropdownMenuItem data-testid="portal-switch-management" onClick={() => handleSwitch("MANAGEMENT")} className="gap-2">
          <Briefcase size={14} className="text-blue-600" /> {t("portal.managementPortal")}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="portal-switch-staff" onClick={() => handleSwitch("STAFF")} className="gap-2">
          <HardHat size={14} className="text-amber-600" /> {t("portal.staffPortal")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
